import { OptionalTruthyStringSchema } from 'notable-common';
import { z } from 'zod';
import { FlowAsynchronousExportJobBaseSchema, FlowAsynchronousExportJobKind, FlowAsynchronousExportJobNullableStringSchema, FlowAsynchronousExportJobOptionalOrderParamsSchema, FlowAsynchronousExportJobOrderParamsSchema, FlowAsynchronousExportJobPatientParamsSchema, FlowAsynchronousExportJobPayorParamsSchema, FlowAsynchronousExportJobRequiredStringSchema, } from './common.js';
import { FlowExportAuthorizationStatusPayloadSchema, FlowExportAuthorizationStatusPayloadSchemaV2, } from './exportAuthorizationStatus.js';
import { BaseFlowBlockSchema } from '../block.js';
import { FlowBlockType } from '../type.js';
// TODO - Better types for common types such as `jobKind` and `patientSex`
const BaseExportPrevisitPlanningPayloadSchema = z.object({
    patientEhrId: z.string(),
    patientFirstName: z.string(),
    patientLastName: z.string(),
    patientDateOfBirth: z.string(), // TODO - better handling of date in both front and backend
    patientSex: z.string(),
    encounterEhrReferenceValue: z.string(),
    encounterId: z.string().optional(),
    ehrInformationId: z.string().optional(),
    providerUserId: z.string().optional(),
});
export const FlowAsynchronousExportJobBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.AsynchronousExportJob),
    config: z.discriminatedUnion('jobKind', [
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportPrevisitPlanning),
            payload: z.discriminatedUnion('action', [
                BaseExportPrevisitPlanningPayloadSchema.extend({
                    action: z.literal('CREATE'),
                    id: z.string().optional(),
                    content: z.string().min(1),
                    created: z.string().optional(), // TODO - better handling of date in both front and backend
                    fileFormat: z.enum(['RTF', 'TXT']),
                }),
                BaseExportPrevisitPlanningPayloadSchema.extend({
                    action: z.literal('DELETE'),
                    id: z.string().min(1),
                    content: z
                        .string()
                        .max(0)
                        .optional()
                        .transform(() => undefined),
                    created: z
                        .string()
                        .max(0)
                        .optional()
                        .transform(() => undefined),
                    fileFormat: z
                        .string()
                        .max(0)
                        .optional()
                        .transform(() => undefined),
                }),
            ]),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportReferralOrder),
            payload: FlowAsynchronousExportJobOptionalOrderParamsSchema.extend({
                patientFirstName: FlowAsynchronousExportJobRequiredStringSchema,
                patientLastName: FlowAsynchronousExportJobRequiredStringSchema,
                patientDateOfBirth: FlowAsynchronousExportJobRequiredStringSchema,
                patientSex: FlowAsynchronousExportJobRequiredStringSchema,
                referredByProviderFirstName: FlowAsynchronousExportJobRequiredStringSchema,
                referredByProviderLastName: FlowAsynchronousExportJobRequiredStringSchema,
                referredByProviderNpi: FlowAsynchronousExportJobRequiredStringSchema,
                rawDiagnoses: FlowAsynchronousExportJobRequiredStringSchema,
                referralComment: OptionalTruthyStringSchema,
                referralPriority: OptionalTruthyStringSchema,
                orderSpecificQuestions: OptionalTruthyStringSchema,
                department: OptionalTruthyStringSchema,
                expectedProcedureDate: OptionalTruthyStringSchema,
                orderClass: OptionalTruthyStringSchema,
                timezone: OptionalTruthyStringSchema,
                procedures: z.union([
                    z
                        .array(z.object({
                        description: FlowAsynchronousExportJobRequiredStringSchema,
                        cptCode: OptionalTruthyStringSchema,
                        areaOfInterest: OptionalTruthyStringSchema,
                    }))
                        .optional(),
                    OptionalTruthyStringSchema,
                ]),
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportReferralDocument),
            payload: FlowAsynchronousExportJobOptionalOrderParamsSchema.extend({
                patientFirstName: FlowAsynchronousExportJobRequiredStringSchema,
                patientLastName: FlowAsynchronousExportJobRequiredStringSchema,
                patientDateOfBirth: FlowAsynchronousExportJobRequiredStringSchema,
                patientSex: FlowAsynchronousExportJobRequiredStringSchema,
                patientRace: OptionalTruthyStringSchema,
                patientPhoneNumber: OptionalTruthyStringSchema,
                patientAddress: z
                    .object({
                    address: OptionalTruthyStringSchema,
                    unitNumber: OptionalTruthyStringSchema,
                    city: OptionalTruthyStringSchema,
                    state: OptionalTruthyStringSchema,
                    zip: OptionalTruthyStringSchema,
                })
                    .optional(),
                referralPriority: OptionalTruthyStringSchema,
                department: OptionalTruthyStringSchema,
                patientOrderCreated: OptionalTruthyStringSchema,
                referredByProviderEhrId: OptionalTruthyStringSchema,
                referredByProviderLastName: OptionalTruthyStringSchema,
                referredByProviderFirstName: OptionalTruthyStringSchema,
                attendingProviderFirstName: OptionalTruthyStringSchema,
                attendingProviderLastName: OptionalTruthyStringSchema,
                attendingProviderNpi: OptionalTruthyStringSchema,
                procedures: z.union([
                    z
                        .array(z.object({
                        description: FlowAsynchronousExportJobRequiredStringSchema,
                        cptCode: OptionalTruthyStringSchema,
                        areaOfInterest: OptionalTruthyStringSchema,
                    }))
                        .optional(),
                    OptionalTruthyStringSchema,
                ]),
                fileName: OptionalTruthyStringSchema,
                fileReference: OptionalTruthyStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportForms),
            payload: z.object({
                previsitFormSetId: FlowAsynchronousExportJobRequiredStringSchema,
                encounterEhrReferenceValue: OptionalTruthyStringSchema,
                templateIds: OptionalTruthyStringSchema,
                overrideAutoExport: z.boolean().optional().default(false),
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportGeneralComment),
            payload: z.object({
                comment: FlowAsynchronousExportJobRequiredStringSchema,
                ehrInformationId: FlowAsynchronousExportJobRequiredStringSchema,
                providerFirstName: FlowAsynchronousExportJobNullableStringSchema,
                providerLastName: FlowAsynchronousExportJobNullableStringSchema,
                practiceTimezone: FlowAsynchronousExportJobNullableStringSchema.optional(),
                patientFirstName: FlowAsynchronousExportJobRequiredStringSchema,
                patientLastName: FlowAsynchronousExportJobRequiredStringSchema,
                patientDateOfBirth: FlowAsynchronousExportJobRequiredStringSchema,
                patientEhrId: FlowAsynchronousExportJobRequiredStringSchema,
                patientSex: FlowAsynchronousExportJobRequiredStringSchema,
                encounterEhrReferenceValue: FlowAsynchronousExportJobRequiredStringSchema,
                encounterId: FlowAsynchronousExportJobRequiredStringSchema,
                providerUserId: OptionalTruthyStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportHccFindings),
            payload: FlowAsynchronousExportJobPatientParamsSchema.extend({
                hccFindingList: FlowAsynchronousExportJobRequiredStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportFindingEvidence),
            payload: z.object({
                ehrInformationId: FlowAsynchronousExportJobRequiredStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExtractPatientInsurance),
            payload: z
                .object({
                providerFirstName: FlowAsynchronousExportJobNullableStringSchema,
                providerLastName: FlowAsynchronousExportJobNullableStringSchema,
            })
                .extend(FlowAsynchronousExportJobPatientParamsSchema.shape),
        }),
        FlowAsynchronousExportJobBaseSchema.extend(FlowExportAuthorizationStatusPayloadSchema.shape),
        FlowAsynchronousExportJobBaseSchema.extend(FlowExportAuthorizationStatusPayloadSchemaV2.shape),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportAuthorizationSubmission),
            payload: FlowAsynchronousExportJobOrderParamsSchema.extend({
                payor: OptionalTruthyStringSchema,
                payorPlan: OptionalTruthyStringSchema,
                specialty: OptionalTruthyStringSchema,
                patient: z.object({
                    firstName: FlowAsynchronousExportJobRequiredStringSchema,
                    lastName: FlowAsynchronousExportJobRequiredStringSchema,
                    dateOfBirth: FlowAsynchronousExportJobRequiredStringSchema,
                }),
                insurance: z
                    .object({
                    memberId: OptionalTruthyStringSchema,
                })
                    .optional()
                    .nullable(),
                submission: z.object({
                    cptCode: FlowAsynchronousExportJobRequiredStringSchema,
                    procedure: OptionalTruthyStringSchema,
                    icd10Codes: FlowAsynchronousExportJobRequiredStringSchema,
                    procedures: z.union([
                        z
                            .array(z.object({
                            id: FlowAsynchronousExportJobRequiredStringSchema,
                            description: FlowAsynchronousExportJobRequiredStringSchema,
                            cptCode: OptionalTruthyStringSchema,
                            areaOfInterest: OptionalTruthyStringSchema,
                            reasonForExam: OptionalTruthyStringSchema,
                            ehrReferenceValue: OptionalTruthyStringSchema,
                            rawDiagnoses: OptionalTruthyStringSchema,
                            standardProcedureId: FlowAsynchronousExportJobNullableStringSchema,
                            patientOrderId: OptionalTruthyStringSchema,
                            diagnoses: z
                                .array(z.object({
                                description: FlowAsynchronousExportJobRequiredStringSchema,
                                icd10Codes: z
                                    .array(FlowAsynchronousExportJobRequiredStringSchema)
                                    .nullable()
                                    .optional(),
                            }))
                                .optional(),
                        }))
                            .optional(),
                        OptionalTruthyStringSchema,
                    ]),
                    diagnoses: FlowAsynchronousExportJobRequiredStringSchema,
                    reasonForExam: OptionalTruthyStringSchema,
                    referredByProvider: z.object({
                        firstName: FlowAsynchronousExportJobRequiredStringSchema,
                        lastName: FlowAsynchronousExportJobRequiredStringSchema,
                        npi: FlowAsynchronousExportJobRequiredStringSchema,
                        phoneNumber: FlowAsynchronousExportJobRequiredStringSchema,
                    }),
                    siteOfService: z
                        .object({
                        name: OptionalTruthyStringSchema,
                        zipcode: OptionalTruthyStringSchema,
                        npi: OptionalTruthyStringSchema,
                        address: OptionalTruthyStringSchema,
                    })
                        .optional()
                        .nullable(),
                    practice: z.object({
                        taxId: FlowAsynchronousExportJobRequiredStringSchema,
                        phoneNumber: FlowAsynchronousExportJobRequiredStringSchema,
                        address: z.object({
                            address: OptionalTruthyStringSchema,
                            city: OptionalTruthyStringSchema,
                            state: z.string(),
                            zip: OptionalTruthyStringSchema,
                        }),
                    }),
                    serviceDate: FlowAsynchronousExportJobRequiredStringSchema,
                    referredByDepartment: OptionalTruthyStringSchema,
                    referredToDepartment: OptionalTruthyStringSchema,
                    patientPhoneNumber: OptionalTruthyStringSchema,
                    department: z
                        .object({
                        contactName: OptionalTruthyStringSchema,
                        name: OptionalTruthyStringSchema,
                        fax: OptionalTruthyStringSchema,
                        phone: OptionalTruthyStringSchema,
                        npi: OptionalTruthyStringSchema,
                        address: OptionalTruthyStringSchema,
                    })
                        .optional()
                        .nullable(),
                }),
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportNoticeOfAdmission),
            payload: FlowAsynchronousExportJobOrderParamsSchema.extend({
                payor: OptionalTruthyStringSchema,
                payorPlan: OptionalTruthyStringSchema,
                specialty: OptionalTruthyStringSchema,
                patient: z.object({
                    firstName: FlowAsynchronousExportJobRequiredStringSchema,
                    lastName: FlowAsynchronousExportJobRequiredStringSchema,
                    dateOfBirth: FlowAsynchronousExportJobRequiredStringSchema,
                }),
                insurance: z
                    .object({
                    memberId: OptionalTruthyStringSchema,
                })
                    .optional()
                    .nullable(),
                submission: z.object({
                    diagnoses: FlowAsynchronousExportJobRequiredStringSchema,
                    reasonForExam: OptionalTruthyStringSchema,
                    referredByProvider: z.object({
                        firstName: FlowAsynchronousExportJobRequiredStringSchema,
                        lastName: FlowAsynchronousExportJobRequiredStringSchema,
                        npi: FlowAsynchronousExportJobRequiredStringSchema,
                        phoneNumber: FlowAsynchronousExportJobRequiredStringSchema,
                    }),
                    providers: FlowAsynchronousExportJobRequiredStringSchema,
                    practice: z.object({
                        taxId: FlowAsynchronousExportJobRequiredStringSchema,
                        phoneNumber: FlowAsynchronousExportJobRequiredStringSchema,
                        address: z.object({
                            address: FlowAsynchronousExportJobNullableStringSchema,
                            city: FlowAsynchronousExportJobNullableStringSchema,
                            state: FlowAsynchronousExportJobRequiredStringSchema,
                            zip: FlowAsynchronousExportJobNullableStringSchema,
                        }),
                    }),
                    serviceDate: FlowAsynchronousExportJobRequiredStringSchema,
                    serviceReviewCategory: FlowAsynchronousExportJobRequiredStringSchema,
                    serviceType: FlowAsynchronousExportJobRequiredStringSchema,
                    serviceFacilityType: FlowAsynchronousExportJobRequiredStringSchema,
                    levelOfService: FlowAsynchronousExportJobRequiredStringSchema,
                    admissionType: FlowAsynchronousExportJobRequiredStringSchema,
                    admissionSource: FlowAsynchronousExportJobNullableStringSchema,
                    referredByDepartment: FlowAsynchronousExportJobNullableStringSchema,
                    referredToDepartment: FlowAsynchronousExportJobNullableStringSchema,
                    patientPhoneNumber: FlowAsynchronousExportJobNullableStringSchema,
                }),
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExtractMedicalNecessity),
            payload: FlowAsynchronousExportJobOrderParamsSchema.extend({
                payor: FlowAsynchronousExportJobRequiredStringSchema,
                procedures: z.union([
                    z.array(z
                        .object({
                        id: FlowAsynchronousExportJobRequiredStringSchema,
                        description: FlowAsynchronousExportJobRequiredStringSchema,
                        cptCode: FlowAsynchronousExportJobNullableStringSchema,
                        rawDiagnoses: FlowAsynchronousExportJobNullableStringSchema,
                        diagnoses: z.array(z
                            .object({
                            description: FlowAsynchronousExportJobRequiredStringSchema,
                            icd10Codes: z.array(FlowAsynchronousExportJobRequiredStringSchema).nullable(),
                        })
                            .strip()),
                    })
                        .strip()),
                    FlowAsynchronousExportJobRequiredStringSchema,
                ]),
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExtractAuthorizationStatus),
            payload: FlowAsynchronousExportJobOrderParamsSchema.extend(FlowAsynchronousExportJobPayorParamsSchema.shape).extend({
                trackingNumber: FlowAsynchronousExportJobNullableStringSchema,
                cptCode: FlowAsynchronousExportJobNullableStringSchema,
                patientAddress: z
                    .object({
                    address: FlowAsynchronousExportJobNullableStringSchema,
                    unitNumber: FlowAsynchronousExportJobNullableStringSchema,
                    city: FlowAsynchronousExportJobNullableStringSchema,
                    state: FlowAsynchronousExportJobNullableStringSchema,
                    zip: FlowAsynchronousExportJobNullableStringSchema,
                })
                    .required(),
                referredByProvider: z
                    .object({
                    firstName: FlowAsynchronousExportJobNullableStringSchema,
                    lastName: FlowAsynchronousExportJobNullableStringSchema,
                    npi: FlowAsynchronousExportJobNullableStringSchema,
                    phoneNumber: FlowAsynchronousExportJobNullableStringSchema,
                })
                    .required(),
                referredByDepartment: OptionalTruthyStringSchema,
                referredToDepartment: OptionalTruthyStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportNoticeOfAdmissionStatus),
            payload: FlowAsynchronousExportJobOrderParamsSchema.extend(FlowAsynchronousExportJobPayorParamsSchema.shape).extend({
                noticeOfAdmissionNote: FlowAsynchronousExportJobRequiredStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportScheduledAppointment),
            payload: z.object({
                patientFirstName: FlowAsynchronousExportJobRequiredStringSchema,
                patientLastName: FlowAsynchronousExportJobRequiredStringSchema,
                patientDateOfBirth: FlowAsynchronousExportJobRequiredStringSchema,
                appointmentForm: z
                    .object({
                    formSetId: FlowAsynchronousExportJobRequiredStringSchema,
                    fields: z.union([
                        z.array(z.object({
                            value: FlowAsynchronousExportJobRequiredStringSchema,
                            fieldName: FlowAsynchronousExportJobRequiredStringSchema,
                            primitive: FlowAsynchronousExportJobRequiredStringSchema,
                        })),
                        FlowAsynchronousExportJobRequiredStringSchema,
                    ], {
                        message: 'Required',
                    }),
                })
                    .required(),
                orderName: FlowAsynchronousExportJobRequiredStringSchema,
                appointmentStartTime: FlowAsynchronousExportJobRequiredStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportSocialHistory),
            payload: z.object({
                previsitFormSetId: FlowAsynchronousExportJobRequiredStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportPatientCareProviders),
            payload: z.object({
                previsitFormSetId: FlowAsynchronousExportJobRequiredStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportCareGapFindings),
            payload: FlowAsynchronousExportJobPatientParamsSchema.extend({
                noteText: FlowAsynchronousExportJobRequiredStringSchema,
            }),
        }),
        FlowAsynchronousExportJobBaseSchema.extend({
            jobKind: z.literal(FlowAsynchronousExportJobKind.ExportPatientInsuranceInformation),
            payload: z.object({
                previsitFormSetId: FlowAsynchronousExportJobRequiredStringSchema,
                encounterEhrReferenceValue: FlowAsynchronousExportJobNullableStringSchema,
                encounterStartTime: FlowAsynchronousExportJobNullableStringSchema,
            }),
        }),
    ]),
});
//# sourceMappingURL=config.js.map